import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { bestPlan, twinLimit } from "@/lib/plan-limits";

// Multi-twin support. A "twin" is a company profile; a user can own several
// (one per allowance in their plan). The active twin is the most recently
// touched company — see resolveCompany in company-access.ts.

type TwinRow = {
  id: string;
  name: string;
  logo_url: string | null;
  is_public: boolean;
  plan: string;
  updated_at: string;
  twin_status: string;
};

/** Every twin the signed-in user can act in: the ones they own plus any
 * workspace they were invited into. The first owned row (most recently
 * updated) is the active one. */
export const listMyTwins = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data: owned, error } = await context.supabase
      .from("companies")
      .select("id, name, logo_url, is_public, plan, updated_at, twin_status")
      .eq("user_id", context.userId)
      .order("updated_at", { ascending: false });
    if (error) throw error;

    const ownedRows = (owned ?? []) as TwinRow[];
    const activeId = ownedRows[0]?.id ?? null;
    const plan = bestPlan(ownedRows.map((c) => c.plan));
    const limit = twinLimit(plan);

    // Workspaces the user only belongs to (not owner) — shown but not counted
    // against the owner's twin allowance.
    const { data: memberships } = await context.supabase
      .from("company_members")
      .select("company_id, role, companies(id, name, logo_url, is_public, plan, twin_status)")
      .eq("user_id", context.userId);
    const memberRows = (memberships ?? [])
      .map((m: any) => m.companies && { ...m.companies, role: m.role })
      .filter(Boolean)
      .filter((c: any) => !ownedRows.some((o) => o.id === c.id));

    return {
      twins: ownedRows.map((c) => ({
        id: c.id,
        name: c.name,
        logo_url: c.logo_url,
        is_public: c.is_public,
        plan: c.plan,
        twin_status: c.twin_status,
        role: "owner" as const,
        active: c.id === activeId,
      })),
      memberships: memberRows.map((c: any) => ({
        id: c.id,
        name: c.name,
        logo_url: c.logo_url,
        is_public: c.is_public,
        plan: c.plan,
        twin_status: c.twin_status,
        role: c.role === "admin" ? ("admin" as const) : ("member" as const),
        active: false,
      })),
      plan,
      limit: Number.isFinite(limit) ? limit : null, // null = unlimited
      ownedCount: ownedRows.length,
      canCreate: ownedRows.length < limit,
    };
  });

/** Create an additional twin, enforcing the plan's twin allowance. New twins
 * inherit the owner's best plan so they run at the level the user pays for. */
export const createTwin = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ name: z.string().trim().min(1).max(120) }).parse(d))
  .handler(async ({ context, data }) => {
    const { data: owned, error: listErr } = await context.supabase
      .from("companies")
      .select("id, plan")
      .eq("user_id", context.userId);
    if (listErr) throw listErr;

    const ownedRows = owned ?? [];
    const plan = bestPlan(ownedRows.map((c: any) => c.plan));
    const limit = twinLimit(plan);
    if (ownedRows.length >= limit) {
      throw new Error(
        `Your ${plan === "free" ? "Free" : plan} plan includes ${limit} ${
          limit === 1 ? "twin" : "twins"
        }. Upgrade to add more.`,
      );
    }

    const { data: created, error } = await context.supabase
      .from("companies")
      .insert({ user_id: context.userId, name: data.name, plan })
      .select("id")
      .single();
    if (error) throw error;
    // Freshly created → most recent updated_at → becomes the active twin.
    return { ok: true, id: created.id };
  });

/** Permanently delete a twin. Owner-only, and never the user's last twin —
 * every account keeps at least one workspace. All dependent data (knowledge,
 * conversations, leads, listings, service requests) is removed by the
 * ON DELETE CASCADE foreign keys on company_id. */
export const deleteTwin = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ companyId: z.string().uuid() }).parse(d))
  .handler(async ({ context, data }) => {
    const { data: owned, error: listErr } = await context.supabase
      .from("companies")
      .select("id")
      .eq("user_id", context.userId);
    if (listErr) throw listErr;
    const ids = (owned ?? []).map((c: any) => c.id);
    if (!ids.includes(data.companyId)) throw new Error("Twin not found.");
    if (ids.length <= 1) {
      throw new Error("You can't delete your only twin. Create another one first.");
    }

    const { error } = await context.supabase
      .from("companies")
      .delete()
      .eq("id", data.companyId)
      .eq("user_id", context.userId);
    if (error) throw error;
    return { ok: true };
  });

/** Switch the active twin by touching its updated_at, which resolveCompany
 * uses to decide which company every dashboard query works against. */
export const setActiveTwin = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ companyId: z.string().uuid() }).parse(d))
  .handler(async ({ context, data }) => {
    // Only owned twins can be activated by touching updated_at (RLS).
    const { data: owned } = await context.supabase
      .from("companies")
      .select("id")
      .eq("user_id", context.userId)
      .eq("id", data.companyId)
      .maybeSingle();
    if (!owned) throw new Error("Twin not found.");

    const { error } = await context.supabase
      .from("companies")
      .update({ updated_at: new Date().toISOString() })
      .eq("id", data.companyId)
      .eq("user_id", context.userId);
    if (error) throw error;
    return { ok: true, id: data.companyId };
  });
